import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";

export interface AppNotification {
  id: string;
  organization_id: string;
  user_id: string;
  type: string;
  title: string;
  message: string;
  link: string | null;
  is_read: boolean;
  created_at: string;
}

// Use any-cast because the generated types file hasn't regenerated yet
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export function useNotifications() {
  const { currentOrganization } = useOrganization();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUserId(session?.user?.id ?? null);
    });
  }, []);

  const fetchNotifications = useCallback(async () => {
    if (!userId || !currentOrganization) return;
    const { data, error } = await db
      .from("notifications")
      .select("*")
      .eq("user_id", userId)
      .eq("organization_id", currentOrganization.id)
      .order("created_at", { ascending: false })
      .limit(50);
    if (!error && data) {
      setNotifications(data as AppNotification[]);
    }
    setLoading(false);
  }, [userId, currentOrganization]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Realtime: new notifications — org-scoped channel name so the
  // realtime.messages RLS policy matches the user's org membership.
  useEffect(() => {
    if (!userId || !currentOrganization) return;
    const channel = supabase
      .channel(`notifications:${currentOrganization.id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${userId}` },
        (payload) => {
          setNotifications((prev) => [payload.new as AppNotification, ...prev]);
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "notifications", filter: `user_id=eq.${userId}` },
        (payload) => {
          setNotifications((prev) =>
            prev.map((n) => (n.id === payload.new.id ? (payload.new as AppNotification) : n))
          );
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [userId, currentOrganization]);

  const markAsRead = useCallback(async (notificationId: string) => {
    await db.from("notifications").update({ is_read: true }).eq("id", notificationId);
    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n))
    );
  }, []);

  const markAllAsRead = useCallback(async () => {
    if (!userId || !currentOrganization) return;
    await db
      .from("notifications")
      .update({ is_read: true })
      .eq("user_id", userId)
      .eq("organization_id", currentOrganization.id)
      .eq("is_read", false);
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
  }, [userId, currentOrganization]);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return { notifications, loading, unreadCount, markAsRead, markAllAsRead, refetch: fetchNotifications };
}
